import { useCallback, useEffect } from 'react';
import { Form, message } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useClosingPackageCommands } from './useClosingPackageCommands';
import { useClosingPackageTaskModalData } from './useClosingPackageTaskModalData';
import {
  buildClientApprovalBody,
  getClientApprovalSuccessMessage,
  validateClientApprovalSubmit,
} from '../lib/clientApprovalModalValidation';
import type { ClosingPackageApprovalResult } from '../types';
import type { ClientApprovalSource } from '../lib/clientApprovalModalValidation';
import { mapClosingPackageApiError } from '../lib/errorMapping';

export type ClientApprovalModalFormValues = {
  registeredAt: Dayjs;
  result: ClosingPackageApprovalResult;
  source: ClientApprovalSource;
  rejectionReason?: string;
  comment: string;
};

export type UseClientApprovalModalOptions = {
  applicationId: number;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
};

export function useClientApprovalModal({
  applicationId,
  open,
  onClose,
  onSuccess,
}: UseClientApprovalModalOptions) {
  const [form] = Form.useForm<ClientApprovalModalFormValues>();

  const { projection, contextView, contextLoading } = useClosingPackageTaskModalData({
    applicationId,
    open,
  });

  const { registerApproval, states } = useClosingPackageCommands();

  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }

    form.setFieldsValue({
      registeredAt: dayjs(),
      source: 'EMAIL',
      comment: '',
      rejectionReason: '',
    });
  }, [form, open, applicationId]);

  const handleSubmit = useCallback(async () => {
    if (!projection) {
      message.error('Не удалось загрузить данные комплекта');
      return;
    }

    let values: ClientApprovalModalFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const validationIssue = validateClientApprovalSubmit({
      eventAt: values.registeredAt,
      result: values.result,
      source: values.source,
      rejectionReason: values.rejectionReason,
      comment: values.comment,
    });

    if (validationIssue) {
      message.error(validationIssue.message);
      return;
    }

    try {
      await registerApproval({
        applicationId,
        body: buildClientApprovalBody({
          result: values.result,
          source: values.source,
          rejectionReason: values.rejectionReason,
          comment: values.comment,
          registeredAt: values.registeredAt.toISOString(),
        }),
      }).unwrap();

      message.success(getClientApprovalSuccessMessage(values.result));
      await onSuccess?.();
      onClose();
    } catch (error) {
      message.error(mapClosingPackageApiError(error));
    }
  }, [applicationId, form, onClose, onSuccess, projection, registerApproval]);

  return {
    form,
    projection,
    contextView,
    contextLoading,
    submitting: states.registerApproval.isLoading,
    handleSubmit,
  };
}

export type UseClientApprovalModalResult = ReturnType<typeof useClientApprovalModal>;
